import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FiEdit2, FiPlus, FiSearch, FiTrash2, FiX } from 'react-icons/fi';
import { addHours, format } from 'date-fns';
import { ThemeContext } from 'styled-components';

import {
  Emissora,
  Programa,
  OrientacaoExibicao as IOrientacaoExibicao,
  Cliente,
} from 'pages/@types';
import api from 'services/api';
import { show } from 'store/modules/dialog/actions';
import { FormatosOrientacaoExibOptions, Role } from 'utils/variables';
import { Container, Filter, View } from './styles';
import { Dictionary, EventApi } from '@fullcalendar/react';
import AppError from 'utils/AppError';
import { usePermit } from 'utils/hooks/usePermit';

import Calendar, { Event } from 'components/Calendar';
import Card from 'components/Card';
import Button from 'components/Button';
import SelectWithoutForm from 'components/Select/withoutForm';
import OrientacaoExibicaoStore from './store';

export interface SelectOption {
  value: number;
  label: string;
}
export interface EmissoraOption {
  id: number;
  label: string;
}

export default function OrientacaoExibicao() {
  // USE STATE
  const [emissoras, setEmissoras] = useState<EmissoraOption[]>([]);
  const [programas, setProgramas] = useState<SelectOption[]>([]);
  const [clientes, setClientes] = useState<SelectOption[]>([]);
  const [datas, setDatas] = useState<{
    datainicio?: Date;
    datafim?: Date;
  }>({});
  const [loadingFormOptions, setLoadingFormOptions] = useState(false);
  const [filterView, setFilterView] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [initialData, setInitialData] = useState({});
  const [clienteFilter, setClienteFilter] = useState(0);
  const [selected, setSelected] = useState<Dictionary>();
  const [deleteId, setDeleteId] = useState(0);
  const [events, setEvents] = useState<Event[]>([]);

  // USE PERMIT
  const hasOrientacaoExibEdicao = usePermit([Role.OPEC], []);

  // USE CONTEXT
  const themeContext = useContext(ThemeContext);

  // REACT REDUX
  const { emissora } = useSelector(
    (state: { usuario: { emissora: { value: number; label: string } } }) =>
      state.usuario,
  );
  const { callback: callbackDialog } = useSelector(
    (state: { dialog: { callback: { success: { action: string } } } }) =>
      state.dialog,
  );
  const dispatch = useDispatch();
  // USE CALLBACK
  const handleNovaObservacao = useCallback(() => {
    setEditMode(false);
    setSelected({});
    setFilterView(false);
    setInitialData({});
  }, []);

  const handleChangeDate = useCallback((datainicio: Date, datafim: Date) => {
    datainicio && datafim && setDatas({ datainicio, datafim });
  }, []);

  const handleEventClick = useCallback((e: EventApi) => {
    const eventInfo = e.extendedProps;
    setSelected({ ...eventInfo, id: e.id });
    setFilterView(false);
    setEditMode(false);
  }, []);

  const handleDelete = useCallback(
    id => {
      setDeleteId(id);
      const optionBase = {
        type: 'question',
        action: 'delete',
        contentHTML: `Remover observação <b>${id}</b>?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/orientacaoexibicao/${id}`,
          method: api.delete,
        },
        haveCallback: true,
        successMessage: 'Observação removida!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(optionBase));
    },
    [dispatch],
  );

  useEffect(() => {
    const controller = new AbortController();
    async function loadFormOptions() {
      setLoadingFormOptions(true);
      try {
        const [
          responseEmissoras,
          responseProgramas,
          responseClientes,
        ] = await Promise.all([
          api.get(`/emissoras`, {
            signal: controller.signal,
          }),
          api.get('/programas', {
            signal: controller.signal,
          }),
          api.get('/clientes', {
            signal: controller.signal,
          }),
        ]);
        if (
          responseEmissoras?.data &&
          responseProgramas?.data &&
          responseClientes?.data
        ) {
          setEmissoras(
            responseEmissoras.data.map((emi: Emissora) => ({
              id: emi.id,
              label: emi.nome,
            })),
          );
          setProgramas(
            responseProgramas.data.map((p: Programa) => ({
              value: p.id,
              label: p.mnemonico,
            })),
          );
          setClientes(
            responseClientes.data.map((c: Cliente) => ({
              value: c.id,
              label: c.mnemonico,
            })),
          );
        } else
          toast.error('Falha de comunicação com o sistema', {
            toastId: 'API Offline',
          });
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      } finally {
        setLoadingFormOptions(false);
      }
    }
    loadFormOptions();
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    async function loadOrientacaoExibicao() {
      try {
        const response = await api.get('/orientacaoexibicao', {
          signal: controller.signal,
          params: {
            datainicio: datas.datainicio,
            datafim: datas.datafim,
            ...(clienteFilter
              ? { cliente: clienteFilter }
              : { emissora: emissora.value }),
          },
        });
        if (response?.data) {
          setEvents(
            response.data.map((o: IOrientacaoExibicao) => ({
              ...o,
              title: o.formato.replace(',', '+'),
              start: addHours(new Date(o.data), 3),
              end: addHours(new Date(o.data), 3),
              allDay: true,
              ...(o.formato.includes(',') && {
                backgroundColor: themeContext?.calendar.multiploformato,
              }),
              borderColor: 'transparent',
            })),
          );
        } else toast.error('Falha de comunicação com o sistema');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message)
            toast.error(appError.message, {
              toastId: appError.message,
            });
          else toast.error('Erro inesperado');
        }
      }
    }
    // Não carregar caso as datas ainda não tenham sido inicializadas pelo calendário
    if (datas.datainicio && datas.datafim) loadOrientacaoExibicao();
    return () => {
      controller.abort();
    };
  }, [
    emissora.value,
    datas,
    clienteFilter,
    themeContext?.calendar.multiploformato,
  ]);

  // Limpando evento deletado do calendário
  useEffect(() => {
    if (
      callbackDialog &&
      callbackDialog.success &&
      callbackDialog.success.action === 'success' &&
      deleteId
    ) {
      setEvents(prevState => prevState.filter(p => p.id != deleteId));
      handleNovaObservacao();
    }
  }, [callbackDialog, deleteId, handleNovaObservacao]);

  function ViewComponent() {
    if (!selected) return null;
    return (
      <View>
        <div>
          <header>
            <div>
              <h3>ORIENTAÇÃO {selected.id}</h3>
              <Button
                fill
                color="font"
                round
                onClick={() => handleDelete(selected.id)}
                disabled={!hasOrientacaoExibEdicao}
              >
                <FiTrash2 />
              </Button>
              <Button
                fill
                color="font"
                round
                onClick={() => {
                  setEditMode(true);
                  setInitialData({
                    ...selected,
                    formato: selected.formato.split(','),
                    emissoras: [selected.emissora.id],
                    break: {
                      value: selected.break || '',
                      label: selected.break,
                    },
                    programa: programas.find(
                      p => p.value === selected.programa,
                    ),
                    cliente: clientes.find(
                      p => p.value === selected.cliente.id,
                    ),
                    ...(selected.cliente2 && {
                      cliente2: clientes.find(
                        p => p.value === selected.cliente2.id,
                      ),
                    }),
                  });
                }}
                disabled={
                  !hasOrientacaoExibEdicao ||
                  !selected.cliente ||
                  loadingFormOptions
                }
              >
                <FiEdit2 />
              </Button>
            </div>
            <div>
              <Button
                fill
                color="font"
                round
                onClick={() => {
                  setSelected({});
                  setFilterView(true);
                }}
                tooltip="Buscar por cliente"
              >
                <FiSearch />
              </Button>
              <Button
                onClick={() => handleNovaObservacao()}
                disabled={!hasOrientacaoExibEdicao}
              >
                <FiPlus />
                NOVA OBSERVAÇÃO
              </Button>
            </div>
          </header>
          <div>
            <section>
              <h5>Data:</h5>
            </section>
            <h4>
              {format(addHours(new Date(selected.data), 3), 'dd/MM/yyyy')}
            </h4>
            <section>
              <h5>Exibidora:</h5>
            </section>
            <h4>{emissoras.find(e => e.id === selected.emissora.id)?.label}</h4>
            <section>
              <h5>Formato:</h5>
            </section>
            <h4>
              {FormatosOrientacaoExibOptions.filter(o =>
                selected.formato.includes(o.id),
              )
                .map(f => f.label)
                .join(' + ')}
            </h4>
            {selected.programa && (
              <div>
                <section>
                  <h5>Programa:</h5>
                </section>
                <h4>
                  {programas.find(p => p.value === selected.programa)?.label}
                  {selected.break && ` (${selected.break})`}
                </h4>
              </div>
            )}
            {selected.titulo && (
              <div>
                <section>
                  <h5>Comercial 1:</h5>
                </section>
                <h4>
                  {selected.codmaterial && `${selected.codmaterial} | `}
                  {selected.titulo} - <i>({selected.cliente.mnemonico})</i>
                  {selected.duracao && ` - ${selected.duracao}s`}
                </h4>
              </div>
            )}
            {selected.titulo2 && (
              <div>
                <section>
                  <h5>Comercial 2:</h5>
                </section>
                <h4>
                  {selected.codmaterial2 && `${selected.codmaterial2} | `}
                  {selected.titulo2} - <i>({selected.cliente2.mnemonico})</i>
                  {selected.duracao2 && ` - ${selected.duracao2}s`}
                </h4>
              </div>
            )}
            {selected.observacao && (
              <div>
                <section>
                  <h5>Observações:</h5>
                </section>
                <h4> {selected.observacao}</h4>
              </div>
            )}
          </div>
        </div>
        <footer>
          <p>
            {selected.updated_at
              ? `Editado por ${selected.criador.nome} em ${format(
                  new Date(selected.updated_at),
                  "dd/MM/yyyy' às 'HH:mm",
                )}`
              : `Criado por ${selected.criador.nome} em ${format(
                  new Date(selected.created_at),
                  "dd/MM/yyyy' às 'HH:mm",
                )}`}
          </p>
        </footer>
      </View>
    );
  }
  return (
    <Container>
      <Card>
        <Calendar
          handleEventClick={handleEventClick}
          handleChangeDate={handleChangeDate}
          events={events}
        />
      </Card>
      <Card>
        {selected && Object.keys(selected).length != 0 && !editMode ? (
          <ViewComponent />
        ) : filterView ? (
          <Filter>
            <header>
              <h3>Busca por Cliente</h3>
              <Button
                fill
                color="font"
                round
                onClick={() => {
                  setSelected({});
                  setInitialData({});
                  setFilterView(false);
                  setClienteFilter(0);
                }}
                tooltip="Sair da busca por cliente"
                tooltipPosition="left"
              >
                <FiX />
              </Button>
            </header>
            <SelectWithoutForm
              placeholder="Selecione o cliente"
              disabled={loadingFormOptions}
              options={clientes}
              onChange={(c: SelectOption) => setClienteFilter(c.value)}
            />
          </Filter>
        ) : (
          hasOrientacaoExibEdicao && (
            <OrientacaoExibicaoStore
              loadingFormOptions={loadingFormOptions}
              emissora={emissora}
              emissoras={emissoras}
              editMode={editMode}
              initialData={initialData}
              setEvents={setEvents}
              clientes={clientes}
              programas={programas}
              setSelected={setSelected}
              setInitialData={setInitialData}
              setEditMode={setEditMode}
            />
          )
        )}
      </Card>
    </Container>
  );
}
